import React from "react";
import {
  Box,
  Heading,
  Text,
  List,
  ListItem,
  VStack,
  Divider,
} from "@chakra-ui/react";
import Seo from "../../seo/Seo";
import FaqSection from "../FaqSection";
import { medicalWebPageSchema, serviceSchema } from "../../seo/schema";
import { absoluteUrl } from "../../seo/siteConfig";

// Rendered by <FaqSection> and emitted as FAQPage JSON-LD by <Seo>.
// Keep the two in sync by only editing this array.
const FAQS = [
  {
    q: "Is a root canal procedure painful?",
    a: "No, modern anesthesia makes the procedure pain-free. Any discomfort afterward can usually be managed with pain medication.",
  },
  {
    q: "How long does a root canal take?",
    a: "The procedure typically takes between 1 to 2 hours, depending on the complexity of the tooth and the infection.",
  },
  {
    q: "Will I need a crown after a root canal?",
    a: "Yes, a crown is often recommended to restore the tooth’s function and protect it from future damage.",
  },
  {
    q: "Can a tooth with a root canal get infected again?",
    a: "While rare, reinfection can occur. If it does, your dentist may need to perform a retreatment.",
  },
  {
    q: "How much does root canal treatment cost?",
    a: "The cost varies depending on the tooth being treated and the extent of the damage. It is often more affordable than extraction and implant options.",
  },
  {
    q: "How soon can I eat after a root canal?",
    a: "You should avoid chewing on the treated tooth until it has been restored with a crown.",
  },
  {
    q: "Can I drink coffee after a root canal?",
    a: "Yes, but avoid drinking hot beverages for a few days if you experience sensitivity.",
  },
  {
    q: "What happens if I don’t get a root canal?",
    a: "If left untreated, the infection can spread, leading to abscesses, severe pain, and possible tooth loss.",
  },
  {
    q: "Can a root canal be performed on any tooth?",
    a: "Most teeth can undergo a root canal, but your dentist will evaluate whether it is the best option for your specific case.",
  },
  {
    q: "Is it safe to undergo a root canal during pregnancy?",
    a: "Root canal treatment can be done during pregnancy, but you should inform your dentist so they can take necessary precautions.",
  },
];

const PAGE_PATH = "/root-canal-treatment";
const PAGE_URL = absoluteUrl(PAGE_PATH);

const RootCanalTreatment = () => {
  return (
    <Box bg="#fceee5">
      <Seo
        path={PAGE_PATH}
        title="Root Canal Treatment in Jaipur | Painless RCT"
        description="Advanced, pain-free root canal treatment in Vaishali Nagar, Jaipur by Dr. Aishwarya Sarda, MDS in Endodontics. Save your natural tooth — book today."
        type="article"
        faqs={FAQS}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Root Canal Treatment", path: PAGE_PATH },
        ]}
        schemas={[
          medicalWebPageSchema({
            url: PAGE_URL,
            title: "Root Canal Treatment in Jaipur | Painless RCT",
            description: "Advanced, pain-free root canal treatment in Vaishali Nagar, Jaipur by Dr. Aishwarya Sarda, MDS in Endodontics. Save your natural tooth — book today.",
            procedureName: "Root Canal Treatment",
          }),
          serviceSchema({
            url: PAGE_URL,
            name: "Root Canal Treatment",
            description: "Advanced, pain-free root canal treatment in Vaishali Nagar, Jaipur by Dr. Aishwarya Sarda, MDS in Endodontics. Save your natural tooth — book today.",
          }),
        ]}
      />
      <Box maxW="4xl" mx="auto" py={8} px={4} marginTop="80px">
        <Heading
          as="h1"
          fontSize={{ base: "2xl", md: "5xl" }}
          mb={6}
          textAlign="center"
          fontWeight="light"
        >
          Root Canal Treatment: Everything You Need to Know
        </Heading>
        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="normal" mb={4}>
          Root canal treatment (RCT) removes infected or inflamed pulp from inside a tooth, disinfects the canals and seals them, so the natural tooth can be kept instead of extracted. It is carried out under local anaesthesia and is usually followed by a crown to protect the treated tooth.
        </Text>
        <Text fontSize="sm" color="gray.600" mb={4} fontStyle="italic">
          By Dr. Aishwarya Sarda, MDS — Conservative Dentistry & Endodontics
        </Text>
        <Divider mb={6} />
        <VStack spacing={4} align="stretch">
          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Overview of Root Canal Treatment
            </Heading>
            <Text mt={2} fontWeight="light">
              Root canal treatment (RCT) is a dental procedure used to treat
              infections or damage to the pulp of a tooth, which is the soft
              tissue inside the tooth that contains nerves, blood vessels, and
              connective tissue. If this pulp becomes infected or inflamed due
              to deep decay, trauma, or infection, it can lead to severe pain
              and, if left untreated, could cause the tooth to abscess. Root
              canal treatment is a common and effective way to save a tooth and
              alleviate the pain caused by these issues.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              What is Root Canal Treatment (RCT)?
            </Heading>
            <Text mt={2} fontWeight="light">
              Root canal treatment, often referred to as RCT, involves removing
              the infected or damaged pulp from the tooth, cleaning the interior
              of the tooth, and then sealing it to prevent further infection. It
              is a crucial dental procedure that allows patients to retain their
              natural tooth rather than opting for extraction. The goal of the
              treatment is to save the tooth, alleviate pain, and restore
              function.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Why Root Canal Treatment?
            </Heading>
            <Text mt={2} fontWeight="light">
              Root canal treatment is needed when the tooth pulp becomes damaged
              or infected, leading to pain, sensitivity, and potential
              complications. Some of the primary causes for needing RCT include:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem>
                <Text mt={2} fontWeight="light">
                  Deep tooth decay
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  Repeated dental procedures on the same tooth
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  A cracked or chipped tooth
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  Trauma to the tooth that damages the pulp
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  Infection due to untreated cavities or oral health issues
                </Text>
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              By undergoing RCT, you can save your natural tooth and avoid the
              need for an extraction, which can be more complicated and costly
              in the long term.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Root Canal vs Extraction
            </Heading>
            <Text mt={2} fontWeight="light">
              Many patients wonder whether it’s better to undergo root canal
              treatment or simply opt for tooth extraction. Here’s a comparison:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Root Canal:</strong> The tooth is preserved, and the
                natural tooth structure remains intact. The procedure eliminates
                infection, reduces pain, and allows the tooth to function
                normally after healing.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Extraction:</strong> The tooth is removed entirely,
                which may require a replacement option such as a dental implant,
                bridge, or partial denture. Extraction can lead to bone loss in
                the jaw and may affect the alignment of surrounding teeth.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              In most cases, saving the tooth with root canal treatment is the
              better option for long-term oral health.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Root Canal Treatment Symptoms
            </Heading>
            <Text mt={2} fontWeight="light">
              If you’re experiencing any of the following symptoms, it could
              indicate that you need root canal treatment:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                Severe tooth pain or sensitivity, especially when chewing or
                touching the tooth
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                Prolonged tooth sensitivity to hot or cold temperatures
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                Swelling or tenderness in the gums near the affected tooth
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                A pimple or abscess on the gums near the infected tooth
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                Discoloration or darkening of the tooth
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                Pain that gets worse at night or with pressure
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              If you experience any of these symptoms, it’s important to see a
              dentist as soon as possible to avoid further complications.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Consequences of Delaying Root Canal Treatment
            </Heading>
            <Text mt={2} fontWeight="light">
              Delaying root canal treatment can lead to a variety of
              complications, including:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Tooth Abscess:</strong> The infection can spread to the
                surrounding tissue, leading to an abscess or pus-filled pocket,
                which can be extremely painful and may require more invasive
                treatment.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Tooth Loss:</strong> Without proper treatment, the tooth
                could weaken and eventually fall out, necessitating extraction.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Bone Loss:</strong> If the infection spreads to the bone
                around the tooth, it can cause bone loss, which could complicate
                any future dental work like implants or bridges.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Systemic Infection:</strong> In severe cases, untreated
                infection in the tooth could spread throughout the body,
                potentially causing serious health issues.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Root Canal Procedure
            </Heading>
            <Text mt={2} fontWeight="light">
              The root canal procedure typically involves several steps:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Examination and X-ray:</strong> Your dentist will
                examine the tooth and may take an X-ray to assess the extent of
                the infection or damage to the pulp.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Anesthesia:</strong> Local anesthesia is administered to
                numb the area around the tooth to ensure you don’t feel any pain
                during the procedure.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Access Opening:</strong> The dentist makes a small
                opening in the crown of the tooth to access the pulp chamber and
                root canals.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Pulp Removal:</strong> The infected or damaged pulp is
                removed from the tooth.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Cleaning and Shaping:</strong> The interior of the tooth
                is cleaned, shaped, and disinfected to remove any remaining
                infection.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Filling and Sealing:</strong> The empty root canals are
                filled with a rubber-like material called gutta-percha, and the
                tooth is sealed to prevent reinfection.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Restoration:</strong> After the root canal is complete,
                the tooth may be restored with a crown or filling to ensure its
                strength and functionality.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Root Canal Post-Treatment Care
            </Heading>
            <Text mt={2} fontWeight="light">
              After undergoing a root canal, it’s important to follow your
              dentist’s post-treatment care instructions to ensure proper
              healing:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                Avoid chewing on the treated tooth until the final restoration
                is placed.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                Take over-the-counter pain medication as advised to manage
                discomfort.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                Brush and floss your teeth gently around the treated area.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                Attend follow-up appointments to check the healing process and
                get the final restoration.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Root Canal Treatment Benefits
            </Heading>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Pain Relief:</strong> The primary benefit of root canal
                treatment is the relief of pain caused by tooth infections and
                decay.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Tooth Preservation:</strong> RCT allows you to retain
                your natural tooth, which is crucial for maintaining bite
                alignment and jawbone health.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Prevention of Further Infection:</strong> The procedure
                removes the infection, preventing it from spreading to
                surrounding teeth and tissues.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Improved Functionality:</strong> Once the root canal is
                completed, the tooth can function as it did before, allowing you
                to eat, chew, and speak normally.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Root Canal Myths and Facts
            </Heading>
            <Text mt={2} fontWeight="light">
              <strong>Myth 1:</strong> Root canal treatment is very painful.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong> With modern anesthesia, root canal
                procedures are typically painless, and the pain is often less
                than the pain caused by the infection.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              <strong>Myth 2:</strong> Root canal treatment causes illness.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong>There is no evidence to suggest that root
                canal treatment causes systemic illness. The procedure is safe
                and helps eliminate infections.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              <strong>Myth 3:</strong> A root canal always fails.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong> Root canal treatments have a high success
                rate when performed properly, with many teeth lasting for years
                after the procedure.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              <strong>Myth 4:</strong> You can always avoid a root canal by
              pulling the tooth.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong> Extraction doesn’t preserve the natural
                tooth, and root canal treatment often saves the tooth, making it
                a better long-term option.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <FaqSection items={FAQS} />
        </VStack>
      </Box>
    </Box>
  );
};

export default RootCanalTreatment;
